import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { productAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { Upload, X, ArrowLeft } from 'lucide-react';

const inputStyle = { width: '100%', padding: '10px 14px', background: 'var(--dark)', border: '1px solid var(--border)', color: 'var(--white)', fontSize: '13px', outline: 'none' };
const labelStyle = { fontSize: '11px', letterSpacing: '0.15em', color: 'var(--white-dim)', display: 'block', marginBottom: '6px' };

const CATEGORIES = ['floral','woody','oriental','fresh','citrus','gourmand','aromatic','chypre'];
const GENDERS = ['men','women','unisex'];
const CONCENTRATIONS = ['EDT','EDP','Parfum','EDC','Cologne'];

const ProductFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [images, setImages] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [existingImages, setExistingImages] = useState([]);

  const [form, setForm] = useState({
    name: '', brand: '', description: '', price: '', salePrice: '',
    category: 'floral', gender: 'unisex', concentration: 'EDP',
    countInStock: '', isFeatured: false,
    size: ['50ml', '100ml'],
    notes: { top: [], middle: [], base: [] },
    tags: [], season: [],
  });

  const [noteInputs, setNoteInputs] = useState({ top: '', middle: '', base: '' });
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    if (!isEdit) return;
    setFetching(true);
    productAPI.getOne(id).then(({ data }) => {
      setForm({
        name: data.name || '', brand: data.brand || '', description: data.description || '',
        price: data.price || '', salePrice: data.salePrice || '',
        category: data.category || 'floral', gender: data.gender || 'unisex',
        concentration: data.concentration || 'EDP', countInStock: data.countInStock || '',
        isFeatured: data.isFeatured || false,
        size: data.size || ['50ml', '100ml'],
        notes: data.notes || { top: [], middle: [], base: [] },
        tags: data.tags || [], season: data.season || [],
      });
      setExistingImages(data.images || []);
    }).catch(() => toast.error('Failed to load product')).finally(() => setFetching(false));
  }, [id, isEdit]);

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setImages(prev => [...prev, ...files]);
    const urls = files.map(f => URL.createObjectURL(f));
    setPreviewUrls(prev => [...prev, ...urls]);
  };

  const removeNewImage = (i) => {
    setImages(prev => prev.filter((_, idx) => idx !== i));
    setPreviewUrls(prev => prev.filter((_, idx) => idx !== i));
  };

  const addNote = (type) => {
    if (!noteInputs[type].trim()) return;
    setForm(f => ({ ...f, notes: { ...f.notes, [type]: [...f.notes[type], noteInputs[type].trim()] } }));
    setNoteInputs(n => ({ ...n, [type]: '' }));
  };

  const removeNote = (type, i) => setForm(f => ({ ...f, notes: { ...f.notes, [type]: f.notes[type].filter((_, idx) => idx !== i) } }));

  const addTag = () => {
    if (!tagInput.trim()) return;
    setForm(f => ({ ...f, tags: [...f.tags, tagInput.trim()] }));
    setTagInput('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.brand || !form.price) { toast.error('Please fill required fields'); return; }
    setLoading(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, val]) => {
        if (['notes', 'size', 'tags', 'season'].includes(key)) {
          formData.append(key, JSON.stringify(val));
        } else {
          formData.append(key, val);
        }
      });
      images.forEach(img => formData.append('images', img));

      if (isEdit) {
        await productAPI.update(id, formData);
        toast.success('Product updated!');
      } else {
        await productAPI.create(formData);
        toast.success('Product created!');
      }
      navigate('/admin/products');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save product');
    } finally { setLoading(false); }
  };

  if (fetching) return <div style={{ padding: '32px' }}><div className="skeleton" style={{ height: '400px' }} /></div>;

  return (
    <div style={{ padding: '32px', maxWidth: '900px' }}>
      <button onClick={() => navigate('/admin/products')} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', color: 'var(--white-dim)', fontSize: '12px', letterSpacing: '0.1em', marginBottom: '24px', cursor: 'pointer' }}>
        <ArrowLeft size={14} /> BACK TO PRODUCTS
      </button>

      <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '36px', fontWeight: 400, marginBottom: '32px' }}>
        {isEdit ? 'Edit Product' : 'Add New Product'}
      </h1>

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          {/* Left Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{ background: 'var(--dark-2)', border: '1px solid var(--border)', padding: '24px' }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', marginBottom: '20px' }}>Basic Info</h3>
              <label style={labelStyle}>PRODUCT NAME *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required style={{ ...inputStyle, marginBottom: '16px' }} placeholder="e.g. Noir Éternel" />
              <label style={labelStyle}>BRAND *</label>
              <input value={form.brand} onChange={e => setForm(f => ({ ...f, brand: e.target.value }))} required style={{ ...inputStyle, marginBottom: '16px' }} placeholder="e.g. Maison Lumière" />
              <label style={labelStyle}>DESCRIPTION *</label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} required rows={5}
                style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.7, marginBottom: '16px' }} placeholder="Describe the fragrance..." />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={labelStyle}>PRICE ($) *</label>
                  <input type="number" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} required style={inputStyle} placeholder="285" min="0" />
                </div>
                <div>
                  <label style={labelStyle}>SALE PRICE ($)</label>
                  <input type="number" value={form.salePrice} onChange={e => setForm(f => ({ ...f, salePrice: e.target.value }))} style={inputStyle} placeholder="Optional" min="0" />
                </div>
              </div>
            </div>

            <div style={{ background: 'var(--dark-2)', border: '1px solid var(--border)', padding: '24px' }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', marginBottom: '20px' }}>Classification</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                <div>
                  <label style={labelStyle}>CATEGORY</label>
                  <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} style={{ ...inputStyle, cursor: 'pointer' }}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>GENDER</label>
                  <select value={form.gender} onChange={e => setForm(f => ({ ...f, gender: e.target.value }))} style={{ ...inputStyle, cursor: 'pointer' }}>
                    {GENDERS.map(g => <option key={g} value={g}>{g.charAt(0).toUpperCase() + g.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>CONCENTRATION</label>
                  <select value={form.concentration} onChange={e => setForm(f => ({ ...f, concentration: e.target.value }))} style={{ ...inputStyle, cursor: 'pointer' }}>
                    {CONCENTRATIONS.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>STOCK</label>
                  <input type="number" value={form.countInStock} onChange={e => setForm(f => ({ ...f, countInStock: e.target.value }))} style={inputStyle} placeholder="100" min="0" />
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.isFeatured} onChange={e => setForm(f => ({ ...f, isFeatured: e.target.checked }))} style={{ accentColor: 'var(--gold)', width: '16px', height: '16px' }} />
                <span style={{ fontSize: '13px' }}>Feature this product on homepage</span>
              </label>
            </div>
          </div>

          {/* Right Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Images */}
            <div style={{ background: 'var(--dark-2)', border: '1px solid var(--border)', padding: '24px' }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', marginBottom: '20px' }}>Images</h3>
              <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '2px dashed var(--border)', padding: '32px', cursor: 'pointer', marginBottom: '16px', transition: 'border-color 0.3s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--gold)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
              >
                <Upload size={24} color="var(--gold)" style={{ marginBottom: '8px' }} />
                <span style={{ fontSize: '12px', color: 'var(--white-dim)' }}>Click to upload images</span>
                <span style={{ fontSize: '11px', color: 'var(--white-faint)', marginTop: '4px' }}>JPG, PNG, WebP (max 5MB each)</span>
                <input type="file" multiple accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
              </label>

              {/* Existing images */}
              {existingImages.length > 0 && (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                  {existingImages.map((url, i) => (
                    <div key={i} style={{ width: '72px', height: '72px', position: 'relative', border: '1px solid var(--border)', overflow: 'hidden' }}>
                      <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <div style={{ position: 'absolute', top: '2px', right: '2px', background: 'rgba(0,0,0,0.7)', padding: '2px' }}>
                        <span style={{ fontSize: '8px', color: 'var(--gold)' }}>SAVED</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* New previews */}
              {previewUrls.length > 0 && (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {previewUrls.map((url, i) => (
                    <div key={i} style={{ width: '72px', height: '72px', position: 'relative', border: '1px solid var(--gold)', overflow: 'hidden' }}>
                      <img src={url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <button type="button" onClick={() => removeNewImage(i)} style={{ position: 'absolute', top: '2px', right: '2px', background: 'rgba(239,68,68,0.8)', border: 'none', color: 'white', width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', borderRadius: '50%' }}>
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Fragrance Notes */}
            <div style={{ background: 'var(--dark-2)', border: '1px solid var(--border)', padding: '24px' }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', marginBottom: '20px' }}>Fragrance Notes</h3>
              {['top', 'middle', 'base'].map(type => (
                <div key={type} style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>{type.toUpperCase()} NOTES</label>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                    <input value={noteInputs[type]} onChange={e => setNoteInputs(n => ({ ...n, [type]: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addNote(type); } }}
                      style={{ ...inputStyle, marginBottom: 0, flex: 1 }} placeholder={`e.g. ${type === 'top' ? 'Bergamot' : type === 'middle' ? 'Rose' : 'Sandalwood'}`} />
                    <button type="button" onClick={() => addNote(type)} style={{ background: 'var(--gold)', color: 'var(--black)', padding: '0 16px', fontSize: '12px', cursor: 'pointer' }}>+</button>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {form.notes[type]?.map((note, i) => (
                      <span key={i} style={{ fontSize: '11px', border: '1px solid var(--gold)', color: 'var(--gold)', padding: '3px 10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {note}
                        <button type="button" onClick={() => removeNote(type, i)} style={{ background: 'none', color: 'var(--gold)', cursor: 'pointer', padding: 0, display: 'flex' }}><X size={10} /></button>
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Tags */}
            <div style={{ background: 'var(--dark-2)', border: '1px solid var(--border)', padding: '24px' }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '20px', marginBottom: '16px' }}>Tags</h3>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                <input value={tagInput} onChange={e => setTagInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                  style={{ ...inputStyle, marginBottom: 0, flex: 1 }} placeholder="e.g. romantic, evening, long-lasting" />
                <button type="button" onClick={addTag} style={{ background: 'var(--gold)', color: 'var(--black)', padding: '0 16px', fontSize: '12px', cursor: 'pointer' }}>+</button>
              </div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {form.tags.map((tag, i) => (
                  <span key={i} style={{ fontSize: '11px', border: '1px solid var(--border)', color: 'var(--white-dim)', padding: '3px 10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {tag}
                    <button type="button" onClick={() => setForm(f => ({ ...f, tags: f.tags.filter((_, idx) => idx !== i) }))} style={{ background: 'none', color: 'var(--white-dim)', cursor: 'pointer', padding: 0 }}><X size={10} /></button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Submit */}
        <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
          <button type="submit" disabled={loading} style={{ background: 'var(--gold)', color: 'var(--black)', padding: '14px 40px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.2em', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.8 : 1 }}>
            {loading ? 'SAVING...' : isEdit ? 'UPDATE PRODUCT' : 'CREATE PRODUCT'}
          </button>
          <button type="button" onClick={() => navigate('/admin/products')} style={{ border: '1px solid var(--border)', background: 'transparent', color: 'var(--white)', padding: '14px 24px', fontSize: '11px', letterSpacing: '0.15em', cursor: 'pointer' }}>
            CANCEL
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProductFormPage;
